
// chat.js: Integración de voz + chat visual con respuesta hablada

import axios from 'axios';
import moment from 'moment';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { chatgpt_logo } from '../assets';
import ChatItem from '../components/ChatItems';
import { ASSISTANT_CHAT_HISTORY_URL, ASSISTANT_CHAT_SEND_URL } from '../constants';
import { AuthContext } from '../context/AuthContext';
import { ChatContext } from '../context/ChatContext';
import { sanitizeForDisplay } from '../utils/textUtils.js';

const Chat = ({ time }) => {
  // Contextos
  const { chatMessage, setChatMessage, setChatShow, setShowChatTime, setChatMenu, threadId, setThreadId } = useContext(ChatContext);
  const { user, course } = useContext(AuthContext);
  

  // Estados del componente
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [responses, setResponses] = useState([]);
  const [chatPower, setChatPower] = useState([]);
  const [files, setFiles] = useState([]);
  const [listening, setListening] = useState(false);
  const [speechEnabled, setSpeechEnabled] = useState(false);  // para apagar audio , default encendido
  const [socraticMode, setSocraticMode] = useState(false);  // modo socratico on off
  const socraticPrompt = "A partir de ahora actúa como un tutor socrático. Haz preguntas en vez de dar respuestas directas. Guía al alumno con razonamiento paso a paso.";



  // Refs
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Cargar historial del chat
  useEffect(() => {
    const fetchChatHistory = async () => {
      try {
        const response = await axios.get(ASSISTANT_CHAT_HISTORY_URL, {
          params: { userid: user.IDalumno },
        });
        setChatPower(response.data.chatHistory);
      } catch (error) {
        console.error('Error fetching chat history:', error.message);
      }
    };
    if (user?.IDalumno) {
      fetchChatHistory();
    }
  }, [user]);

  // Scroll automático cuando hay nuevas respuestas
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [responses, chatPower]);

  // Al cambiar conversación
  useEffect(() => {
    setResponses([]);
    setMessage('');
    setFiles([]);
    adjustTextareaHeight();
    if (textareaRef.current) textareaRef.current.focus();
  }, [chatMessage]);

  
  // Reproduce texto por voz usando Web Speech API
  const speakText = (text) => {
  const synth = window.speechSynthesis;

  // Siempre cancelar cualquier reproducción en curso
  synth.cancel();

  // Solo hablar si está activado
  if (!speechEnabled) return;

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'es-MX';
  synth.speak(utterance);
};






  
// Enviar mensaje al servidor y manejar respuesta
const handleSend = async (forcedMessage) => {
  const finalMessage = forcedMessage || message;
  if (!finalMessage.trim() && !files.length) return;

  const now = new Date();
  const filenames = files.map((file) => file.name);

  // 1️⃣ Mostrar pregunta del usuario inmediatamente
  const tempResponse = {
    message: finalMessage,
    response: 'Procesando respuesta...',
    filenames,
    timestamp: now.toISOString(),
    pending: true, // opcional: lo puedes usar para mostrar un spinner en UI
  };
  setResponses((prev) => [...prev, tempResponse]);

  setLoading(true);
  try {
    const formData = new FormData();
    formData.append('userId', user.IDalumno);
    formData.append('time', time);
    const fullMessage = (socraticMode ? socraticPrompt + "\n\n" : "") + finalMessage + " " + (course?.["NOMBRELARGOCURSO"] || "");
    formData.append('message', fullMessage);
    formData.append('threadId', threadId);
    
    files.forEach((file) => formData.append("files", file));

    const res = await axios.post(ASSISTANT_CHAT_SEND_URL, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });

    const cleanText = sanitizeForDisplay(res.data.answer);

    // 2️⃣ Reemplazar la respuesta "temporal" con la verdadera
    setResponses((prev) => {
      const updated = [...prev];
      updated[updated.length - 1] = {
        ...updated[updated.length - 1],
        response: res.data.answer,
        pending: false,
      };
      return updated;
    });

    speakText(cleanText);

    setMessage('');
    setFiles([]);
    textareaRef.current?.focus();
    if (res.data.threadId) setThreadId(res.data.threadId);
  } catch (err) {
    console.error('Error sending message:', err);
    setResponses((prev) => {
      const updated = [...prev];
      updated[updated.length - 1] = {
        ...updated[updated.length - 1],
        response: 'Error al procesar la respuesta.',
        pending: false,
      };
      return updated;
    });
  } finally {
    if (chatMessage.length === 0) {
      try {
        const response = await axios.get(ASSISTANT_CHAT_HISTORY_URL, {
          params: { userid: user.IDalumno }
        });
        setChatMenu(response.data.menu.reverse());
      } catch (error) {
        console.error('Error fetching chat history:', error.message);
      }
    }
    setLoading(false);
  }
};



  // Inicia una conversación nueva
  const handleNewDialog = () => {
    setShowChatTime(new Date().toISOString());
    setChatMessage([]);
    setChatShow(true);
    setThreadId(null);
    const fetchChatHistory = async () => {
      try {
        const response = await axios.get(ASSISTANT_CHAT_HISTORY_URL, {
          params: { userid: user.IDalumno }
        });
        setChatMenu(response.data.menu);
      } catch (error) {
        console.error('Error fetching chat history:', error.message);
      }
    };
    fetchChatHistory();
  };

  // Cambios de entrada manual
  const handleInputChange = (e) => {
    setMessage(e.target.value);
    adjustTextareaHeight();
  };

  // Subir archivos adjuntos
  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
  };

  // Eliminar archivo
  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  // Enviar con Enter
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Ajuste visual del textarea
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
  };

  // Reconocimiento de voz Web Speech API
  const startVoiceInput = () => {
    const recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition)();
    recognition.lang = 'es-MX';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    setListening(true);

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setMessage(transcript);           // Para que se vea en la UI
      handleSend(transcript);           // Para enviarlo de inmediato al server
      setListening(false);
    };


    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setListening(false);
    };

    recognition.onend = () => {
      setListening(false);
    };

    recognition.start();
  };

  const formatTimestamp = (timestamp) => moment(timestamp).format('MMMM Do YYYY, h:mm:ss a');

  // --- RENDER ---
  return (
    <>
      {/* Historial de mensajes */}
      <div className="flex flex-col flex-grow overflow-y-auto w-full mx-auto p-4 bg-white">
        <div className="flex-grow overflow-y-auto w-full mx-auto">
          {chatMessage.map((item, index) => (
            <ChatItem
              key={`history-${index}`}
              index={index}
              timestamp={formatTimestamp(item.timestamp)}
              title={item.question}
              content={item.answer}
              filenames={item.filenames}
              chatgpt_logo={chatgpt_logo}
            />
          ))}

          {responses.map((item, index) => (
            <ChatItem
              key={`response-${index}`}
              index={index + chatMessage.length}
              timestamp={formatTimestamp(item.timestamp)}
              title={item.message}
              content={item.response}
              filenames={item.filenames}
              chatgpt_logo={chatgpt_logo}
            />
          ))}

          {loading && (
            <div className="flex justify-center items-center py-4">
              <div className="loader">
                Cargando...
                <i className="fas fa-spinner fa-spin text-2xl text-gray-500"></i>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Botones inferiores */}
      <div className="w-4/5 bottom-0 bg-white flex items-center space-x-3 mx-auto text-black text-[15px]">
       <div className="flex-grow" />
       <div className="flex items-end space-x-3 flex-wrap gap-2">
    



         {/* Botón nuevo diálogo */}
         <button
           onClick={handleNewDialog}
            className="p-2 sm:p-3 bg-white-500 text-black"
          >
            <i className="fas fa-broom"></i> &nbsp;
            Nuevo diálogo
         </button>
        </div>
      </div>


      {/* Área de texto + archivos */}
      <div className="w-full p-4">
        <div className="w-full bottom-0 mb-3 bg-white p-3 sm:p-4 flex items-center space-x-3 border rounded-lg border-gray mx-auto">
          <div className="flex-grow">
            {loading ? (
              <div className="loader">
                <i className="fas fa-spinner fa-spin text-2xl text-gray-500"></i> &nbsp;
                Generar mensaje...
              </div>
            ) : (
              <>
                <div className={`flex space-x-2 ${files.length ? "mb-4" : ""}`}>
                  {files.map((file, index) => (
                    <div key={index} className="rounded-lg border p-2 whitespace-nowrap">
                      <i className="fa-regular fa-file p-2 bg-blue-500 me-2 rounded-lg text-white"></i>
                      {file.name}
                      <button onClick={() => removeFile(index)} className="ms-2 cursor-pointer text-xs">
                        ❌
                      </button>
                    </div>
                  ))}
                </div>
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  className="w-full bg-white focus:outline-none focus:border-white-400"
                  rows="1"
                  placeholder="Enviame un mensaje para ayudarte. Soy tu asistente virtual..."
                  style={{ maxHeight: '150px', overflow: 'auto' }}
                />
              </>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {loading ? (
              <button className="p-2 bg-white-500 text-gray">
                <i className="fas fa-stop"></i>
              </button>
            ) : (
              <button onClick={handleSend} className="p-2 bg-white-500 text-black">
                <i className="fas fa-paper-plane"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Chat;
